/*
We can't really meaningfully test this file, as our tests will always be using
a MockedProvider rather than the real apollo setup. The environment-specific
url and token functions also can't really be meaningfully tested.

However, manual testing and end-to-end tests should keep the risk of problems
with this file very low—if there is a problem with the Apollo client, we'll
see that pretty quickly in both cases. So we've decided that it would be best
to have this file ignored for coverage, so we don't get used to ignoring
a lack of coverage and can continue to aim for 100%.
*/

/* istanbul ignore file */

import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import {
  concatPagination,
  relayStylePagination,
} from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";

import { Organisation } from "@src/shared/node/GeneratedDatabaseTypes";
import { SentryLink } from "apollo-link-sentry";
import { BookingTool } from "./GeneratedGraphQL";

const SentryErrorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    for (const error of graphQLErrors) {
      Sentry.withScope((scope) => {
        scope.setTag(
          "graphql.error.code",
          (error.extensions?.code as string) ?? "unknown",
        );
        scope.setExtra("query.path", error.path);
        scope.setExtra(
          "server.stacktrace",
          (
            error.extensions?.exception as { stacktrace?: string[] }
          )?.stacktrace?.join("\n"),
        );
        scope.captureException(
          new Error(`GraphQL Response Error: ${error.message}`),
        );
      });
    }
  }
});

// Export the ApolloProvider component here so we don't have to
// re-import it.
export { ApolloProvider } from "@apollo/client";

/**
 * Get the correct URL to use for calling our GraphQL API based on the
 * runtime environment that this application is bundled for.
 *
 * @returns URL of the GraphQL API to use for the current runtime
 * environment.
 */
export function getGraphUrl(): string {
  const runtimeEnv = process.env.RUNTIME_ENV ?? "development";

  let uri = "http://localhost:8000";

  if (runtimeEnv === "production") {
    uri = "https://graphql.services.appointedd.com";
  } else if (runtimeEnv === "t2") {
    uri = "https://graphql-t2.services.appointedd.com";
  } else if (runtimeEnv === "t1") {
    uri = "https://graphql-t1.services.appointedd.com";
  }

  return uri;
}

/**
 * Get the public facing access token to make a booking tool graphql request
 */
export function getPublicAccessToken(): string {
  const runtimeEnv = process.env.RUNTIME_ENV ?? "development";

  if (runtimeEnv === "production") {
    return "9HzWf7vzQS3pnUMF++dT2G9E8yxY+siKWy54TRyUTqaLOXiC34+78WGXbRA1DoJmcxdn5BaNUXZN6jZYHrPU2a4Zmy5sM8/zWiMZEvT5caH1kbnvZJAozG8zK494s2ttjxwh9p6XNBA9NXpwtjqG3YzrKlINg9G+LCsqvp6vXJA=";
  } else if (runtimeEnv === "t2") {
    return "8hs6VvaFzT5Ood0S7MO8Zdg7wxprzv9ujsOyndj/aS1tY0YC1IsD7j1ZxziKmUfrWS51daPXOUjbPE0/qTDb1a2oN/U5J8M5lFzSoJhFeFaIvPmVsuyihrJMbr0GiFaVaVSaTn4raHTZb1joYzjGkJBl/q5WqtzWPMHphydNnZY=";
  } else if (runtimeEnv === "t1") {
    return "s28ptdelDgT2m9d2X9QVRkwx2pORyUwfHRxVem49LrCoHKAURbnzjOGIRx+ckFXcXawgeA5/PqhgklG1MuPmELBmbs6pWFCsZdwyTNLYwCRG/dNAgSmEKLPenVMgNghNLC85LTEfhSVvABRkDzpzcyVLcLQ50ofMMmTY0eeUXcc=";
  }

  return "b9ETob1IR5riwp1eVEI4C2D0S14rV1S5ZooLGS0oZp/UNdkX5lxcN1B7+djR9S0EgNlCgVmUs2ZA8CH+5L5M7edrfKBpmeQ+9LQmckz0t7Upv/LD5CIIcptmts7tx9BfWbGIP0woQwf1tdHsBES1Ff323V2vfkfsEBrqsP49CSc=";
}

export function createCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      /**
       * Disable cache normalisation for booking questions
       * as legacy booking questions do not have an id, so we
       * can't rely upon the id field for this, as multiple
       * null values will cause collisions.
       *
       * We'll need to be cogniscent of this if we attempt
       * to paginate booking questions at any point.
       */
      BookingQuestion: {
        keyFields: false,
      },
      BookingTool: {
        fields: {
          organisations: relayStylePagination(),
          services: relayStylePagination(),
        },
      },
      Organisation: {
        fields: {
          availabilityDates: concatPagination(),
          availabilityIntervals: concatPagination(),
          services: relayStylePagination(),
        },
      },
      Query: {
        fields: {
          bookingTool: {
            merge: function (
              existing: Partial<BookingTool>,
              incoming: Partial<BookingTool>,
              { mergeObjects },
            ) {
              return mergeObjects(existing, incoming);
            },
          },
          organisation: {
            merge: function (
              existing: Partial<Organisation>,
              incoming: Partial<Organisation>,
              { mergeObjects },
            ) {
              return mergeObjects(existing, incoming);
            },
          },
          service: {
            merge: function (
              existing: Partial<Organisation>,
              incoming: Partial<Organisation>,
              { mergeObjects },
            ) {
              return mergeObjects(existing, incoming);
            },
          },
        },
      },
      Service: {
        fields: {
          resources: relayStylePagination(),
          resourcesForBookingTool: relayStylePagination(),
        },
      },
      ServiceTier: {
        keyFields: false,
      },
      ServiceTierPricePoint: {
        keyFields: false,
      },
    },
  });
}

export function createClient(): ApolloClient<NormalizedCacheObject> {
  type Header = {
    [key: string]: string;
  };

  const graphUrl = getGraphUrl();

  return new ApolloClient({
    cache: createCache(),
    connectToDevTools: process.env.RUNTIME_ENV !== "production",
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    link: setContext((_, { headers }: { headers: Header }) => ({
      headers: { ...headers, "Access-Token": getPublicAccessToken() },
    }))
      .concat(SentryErrorLink)
      .concat(new RetryLink())
      .concat(
        new SentryLink({
          attachBreadcrumbs: {
            includeError: true,
            includeVariables: true,
          },
          uri: graphUrl,
        }),
      )
      .concat(createHttpLink({ uri: graphUrl })),
  });
}
